// Der Technologiebaum als eigene Szene.
//
// Im Vorlaeufer war er ein zweiter Canvas mit handgeschriebenem Pan und Zoom.
// Als Phaser-Szene kommt beides von der Kamera — und die Szene wird in den
// **Ausschnitt des Panels** eingepasst (`setViewport`), sodass HUD und Panel
// weiterhin DOM bleiben. Das ist der Grund, warum die Zweiteilung erhalten
// bleibt: Text und Listen sind im DOM billiger und zugaenglicher, ein Baum mit
// Verbindungslinien auf Canvas.
//
// Knotenzustaende sind **vier**: gesperrt, kaufbar, sichtbar-aber-zu-teuer,
// maximal ausgebaut. Gesperrte Nachbarn werden **verdeckt, nicht ausgeblendet** —
// ein leerer Bereich sieht nach Ende aus, ein Schloss nach Ziel.
//
// Jeder Zustand traegt **drei** Unterschiede, nicht einen: Farbe, Rahmen und
// eine Fortschrittsleiste. Ein einzelnes Merkmal reicht bei 47 Kacheln nicht —
// die erste Fassung unterschied „kaufbar" und „ausgebaut" nur durch zwei
// aehnliche Goldtoene, und der Spieler musste jede Kachel antippen, um zu
// sehen, wo noch etwas geht. Die Leiste ist dabei das ehrlichste Merkmal: sie
// zeigt Stufe und Hoechststufe zugleich, ohne Text und ohne Farbwissen.

import Phaser from 'phaser';

import { bus } from '../core/bus';
import { clamp } from '../core/math';
import { node as nodeById, nodes } from '../data/balancing';
import type { NodeState, TreeNode } from '../data/types';
import { NODE_STYLE, cssColor } from '../gfx/palette';
import { iconTextureKey } from '../gfx/textures';
import {
  TREE_CELL as CELL, TREE_NODE_SIZE as NODE_SIZE, TREE_ZOOM_MAX, TREE_ZOOM_MIN,
  fitZoom, nodeAt, nodeState, treeBounds, treeStatus,
} from '../sim/tree';
import { store } from '../state/store';

/** Bewegung in Bildpunkten, ab der aus einem Tippen ein Ziehen wird. */
const DRAG_SLOP = 8;

/**
 * Masse der Fortschrittsleiste am unteren Rand einer Kachel, in Weltpixeln.
 *
 * `BAR_BOTTOM` haelt sie unter dem Icon: das Icon fuellt 0,66 der Kachel und
 * endet damit bei 38 — eine Leiste weiter oben bekaeme dessen unterste
 * Pixelzeile ueber sich (sie liegt eine Ebene hoeher) und saehe zufaellig
 * eingefaerbt aus.
 */
const BAR_INSET = 4;
const BAR_HEIGHT = 5;
const BAR_BOTTOM = 3;

export interface TreeCallbacks {
  onBuy(id: string): void;
  onHover(node: TreeNode | null, screenX: number, screenY: number): void;
}

export class TechTreeScene extends Phaser.Scene {
  static readonly KEY = 'techtree';

  private gfx!: Phaser.GameObjects.Graphics;
  private readonly icons = new Map<string, Phaser.GameObjects.Image>();
  private readonly labels = new Map<string, Phaser.GameObjects.Text>();
  private callbacks: TreeCallbacks = { onBuy: () => {}, onHover: () => {} };
  /** Ob der Reiter offen ist. Siehe `setOpen` — die Szene laeuft immer mit. */
  private open = false;
  private hovered: TreeNode | null = null;
  /** Der per Fingertipp gewaehlte Knoten. Am Zeigergeraet immer `null`. */
  private selected: TreeNode | null = null;
  private holdTimer = 0;
  /** Gesetzt, sobald ein Zug abgelehnt wurde — bis zum naechsten Druecken. */
  private holdBlocked = false;
  private dragging = false;
  private dragMoved = 0;
  private downX = 0;
  private downY = 0;
  private pinchDist = 0;

  constructor() {
    super({ key: TechTreeScene.KEY, active: false });
  }

  setCallbacks(callbacks: TreeCallbacks): void {
    this.callbacks = callbacks;
  }

  /**
   * Das Halten beenden, weil der Zug abgelehnt wurde.
   *
   * Online aendert sich `store.save` erst mit der Antwort. Waehrend der
   * Laufzeit einer abgelehnten Anfrage fuehrt `nodeState` den Knoten weiter als
   * kaufbar — ohne diese Bremse liefe die Wiederholung mit einer Anfrage je
   * Antwortzeit endlos weiter.
   */
  stopHold(): void {
    this.holdBlocked = true;
  }

  /**
   * Der Baum nimmt Zeiger nur entgegen, solange sein Reiter offen ist.
   *
   * Die Szene laeuft **dauerhaft** mit (siehe `BootScene`), und
   * `scene.setVisible(false)` schaltet nur das **Zeichnen** ab: `update` laeuft
   * weiter, die Zeigerbehandlung bleibt scharf, und die Kamera steht noch auf
   * dem Ausschnitt des geschlossenen Panels. Ohne diesen Riegel traf eine
   * Mausbewegung ueber der Welt weiterhin einen Knoten — der Tooltip erschien
   * ueber dem Spielfeld, und ein Klick kaufte dort sogar eine Stufe.
   *
   * `SpaceScene` haelt denselben Riegel andersherum (`panelOpen`): solange ein
   * Panel offen ist, fliegt das Schiff nicht los. Beide brauchen ihn, weil das
   * Panel den Canvas bewusst **nicht** abschirmt (siehe `style.css`).
   */
  setOpen(open: boolean): void {
    if (open === this.open) return;
    this.open = open;
    if (open) {
      this.redraw();
      return;
    }
    this.cancel();     // ein gehaltener Knopf darf nicht weiterkaufen
    this.clearHover(); // raeumt Auswahl, Hover und den DOM-Tooltip ab
  }

  create(): void {
    this.gfx = this.add.graphics().setDepth(0);
    // Deckend, nicht durchsichtig: der Kamera-Ausschnitt **ist** die Flaeche des
    // Panels, und das DOM darueber laesst sie bewusst frei (siehe style.css).
    this.cameras.main.setBackgroundColor('#0f141c');
    // Kein `centerOn` hier: die Kamera misst jetzt noch den ganzen Canvas, und
    // `setViewport` rechnet den Bildlauf hinterher **nicht** um. Eingepasst
    // wird beim Oeffnen des Panels, in `resetView`.
    this.buildNodes();
    this.bindInput();
    this.scene.setVisible(false);
    // Nur bei offenem Reiter: `main.ts` macht aus jedem `state:changed` ein
    // `tree:refresh`, und das feuert bei praktisch jedem Erzfund. 47 Kacheln
    // neu zu zeichnen, waehrend niemand hinsieht, ist verschenkte Zeit — beim
    // Oeffnen zeichnet `setOpen` ohnehin neu.
    bus.on('tree:refresh', () => { if (this.open) this.redraw(); });
  }

  /** Den Zeichenbereich an den Panel-Ausschnitt binden. */
  setViewportRect(rect: DOMRect): void {
    this.cameras.main.setViewport(
      Math.round(rect.left),
      Math.round(rect.top),
      Math.round(rect.width),
      Math.round(rect.height),
    );
  }

  /**
   * Den ganzen Baum in den Ausschnitt einpassen.
   *
   * Wird beim Oeffnen des Panels gerufen — **nach** `setViewportRect`, denn
   * `centerOn` rechnet mit der Kamerabreite. Ohne das sieht der Spieler eine
   * leere Flaeche neben dem Baum und hat auf dem Handy keinen Weg zurueck.
   */
  resetView(): void {
    const camera = this.cameras.main;
    const bounds = treeBounds(nodes());
    camera.setZoom(fitZoom(bounds, camera.width, camera.height));
    camera.centerOn(bounds.centerX, bounds.centerY);
    this.clearHover();
    this.redraw();
  }

  /**
   * Auswahl, Hover und Tooltip abraeumen.
   *
   * Einmal, weil zwei Aufrufer dasselbe brauchen: das Einpassen beim Oeffnen
   * und das Schliessen des Reiters. Der Tooltip ist ein DOM-Element und haengt
   * an keinem Panel — er verschwindet nur, wenn ihn jemand abmeldet.
   */
  private clearHover(): void {
    this.selected = null;
    this.hovered = null;
    this.callbacks.onHover(null, 0, 0);
  }

  private buildNodes(): void {
    for (const entry of nodes()) {
      const x = entry.x * CELL;
      const y = entry.y * CELL;
      const icon = this.add.image(x, y, iconTextureKey(entry.icon))
        .setDisplaySize(NODE_SIZE * 0.66, NODE_SIZE * 0.66)
        .setDepth(2);
      this.icons.set(entry.id, icon);

      // Die Stufenzahl sitzt in der oberen **rechten** Ecke: unten liegt die
      // Fortschrittsleiste, links die Ecke des kaufbaren Knotens. Jedes
      // Merkmal hat seinen eigenen Platz, sonst verdeckt eines das andere.
      const label = this.add.text(x + NODE_SIZE / 2 - 9.5, y - NODE_SIZE / 2 + 9, '', {
        fontFamily: '"Lucida Console", monospace',
        fontSize: '11px',
        color: '#ffe98a',
      }).setOrigin(0.5, 0.5).setDepth(3);
      this.labels.set(entry.id, label);
    }
    this.redraw();
  }

  private bindInput(): void {
    // Zweiter Zeiger fuers Kneifen — ohne ihn gibt es auf dem Handy keinen Zoom.
    this.input.addPointer(1);

    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (!this.open) return;
      // **Erst nachsehen, dann merken.** Auf einem Zeigergeraet steht der Hover
      // vom Bewegen her schon; auf einem Finger gibt es kein Hover, und ohne
      // diese Zeile weiss der Baum beim Loslassen nicht, was getippt wurde —
      // genau deshalb war er auf dem Handy nicht bedienbar.
      this.updateHover(pointer);
      this.dragging = true;
      this.holdBlocked = false;
      this.dragMoved = 0;
      this.downX = pointer.x;
      this.downY = pointer.y;
    });

    this.input.on('pointerup', (pointer: Phaser.Input.Pointer) => this.release(pointer));
    // Die drei Abbrueche bleiben ungeschuetzt: sie raeumen nur auf, und das
    // darf auch bei geschlossenem Reiter geschehen.
    this.input.on('pointerupoutside', () => this.cancel());
    this.input.on('pointercancel', () => this.cancel());
    this.input.on('gameout', () => this.cancel());

    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (!this.open || this.pinching()) return;
      if (this.dragging && pointer.isDown) {
        // Echte Bildpunkte statt aufsummierter Geschwindigkeit: auf Touch sind
        // die Geschwindigkeitswerte so hoch, dass ein sauberes Tippen als
        // Ziehen durchginge.
        this.dragMoved = Math.abs(pointer.x - this.downX) + Math.abs(pointer.y - this.downY);
        this.cameras.main.scrollX -= pointer.velocity.x / this.cameras.main.zoom / 60;
        this.cameras.main.scrollY -= pointer.velocity.y / this.cameras.main.zoom / 60;
      }
      if (!pointer.wasTouch || this.dragging) this.updateHover(pointer);
    });

    this.input.on('wheel', (_p: unknown, _o: unknown, _dx: number, dy: number) => {
      if (!this.open) return;
      this.zoomBy(dy < 0 ? 1.12 : 0.89);
    });
  }

  private zoomBy(factor: number): void {
    const camera = this.cameras.main;
    camera.setZoom(clamp(camera.zoom * factor, TREE_ZOOM_MIN, TREE_ZOOM_MAX));
  }

  private pinching(): boolean {
    return this.input.pointer1.isDown && this.input.pointer2.isDown;
  }

  private cancel(): void {
    this.dragging = false;
    this.holdTimer = 0;
  }

  /**
   * Loslassen: kaufen, auswaehlen oder nichts.
   *
   * Am Zeigergeraet bleibt es beim Gewohnten — zeigen zeigt, klicken kauft.
   * Am Finger gibt es kein Zeigen, deshalb **waehlt der erste Tipp aus** (und
   * zeigt Wirkung und Kosten), **der zweite kauft**. Blind kaufen waere auf
   * einem Geraet, das keinen Tooltip vorab zeigen kann, eine Falle.
   */
  private release(pointer: Phaser.Input.Pointer): void {
    if (!this.open) return;
    const gezogen = !this.dragging || this.dragMoved > DRAG_SLOP;
    this.cancel();
    if (gezogen || !this.hovered) return;

    if (pointer.wasTouch && this.selected !== this.hovered) {
      this.selected = this.hovered;
      this.showTooltipAt(this.hovered);
      this.redraw();
      return;
    }
    this.callbacks.onBuy(this.hovered.id);
  }

  override update(_time: number, delta: number): void {
    if (!this.open) return;
    if (this.pinching()) {
      this.updatePinch();
      return;
    }
    this.pinchDist = 0;

    // Halten kauft wiederholt: bei vielen Knoten mit mehreren Stufen ist
    // Einzelklicken Arbeit ohne Entscheidung. Am Finger erst, wenn der Knoten
    // ausgewaehlt ist — sonst kaufte ein liegengebliebener Daumen von selbst.
    if (this.holdBlocked || !this.dragging || !this.hovered) return;
    if (this.dragMoved > DRAG_SLOP) return;
    if (this.input.activePointer.wasTouch && this.selected !== this.hovered) return;
    this.holdTimer += delta / 1000;
    if (this.holdTimer > 0.45) {
      this.holdTimer = 0.32;
      // Aussichtsloses wird gar nicht erst gesendet: online wird sonst aus
      // jedem gehaltenen Frame eine echte Anfrage, die der Server mit 409
      // beantwortet — sichtbar als Fehlerflut in der Konsole. `nodeState` ist
      // **dieselbe** Auswertung, aus der Farbe und Legende kommen, also keine
      // zweite Regelquelle. Sie laeuft nur hier im Ausloesezweig, nicht je
      // Frame (Gate FG-14).
      if (nodeState(this.hovered, treeStatus(store.save, store.statCard('enemiesByKind'))) !== 'buyable') return;
      this.callbacks.onBuy(this.hovered.id);
    }
  }

  private updatePinch(): void {
    this.cancel();
    const [a, b] = [this.input.pointer1, this.input.pointer2];
    const dist = Math.hypot(a.x - b.x, a.y - b.y);
    if (this.pinchDist > 0 && dist > 0) this.zoomBy(dist / this.pinchDist);
    this.pinchDist = dist;
  }

  private updateHover(pointer: Phaser.Input.Pointer): void {
    const found = nodeAt(nodes(), pointer.worldX, pointer.worldY);
    if (found !== this.hovered) {
      this.hovered = found;
      this.redraw();
    }
    if (pointer.wasTouch) return;             // am Finger zeigt der Tipp, nicht die Bewegung
    // `pointer.x/y` sind Canvas-Koordinaten; der Canvas fuellt das Fenster.
    this.callbacks.onHover(found, pointer.x, pointer.y);
  }

  /** Den Tooltip an den Knoten heften statt an den Finger, der ihn verdeckt. */
  private showTooltipAt(entry: TreeNode): void {
    const camera = this.cameras.main;
    const x = camera.x + (entry.x * CELL - camera.scrollX - camera.width / 2) * camera.zoom
      + camera.width / 2;
    const y = camera.y + (entry.y * CELL - camera.scrollY - camera.height / 2) * camera.zoom
      + camera.height / 2 - (NODE_SIZE / 2) * camera.zoom;
    this.callbacks.onHover(entry, Math.round(x), Math.round(y));
  }

  /** Neu zeichnen. Liest ausschliesslich — Zeichnen mutiert nichts (Gate FG-17). */
  redraw(): void {
    const g = this.gfx;
    g.clear();
    // Einmal je Neuzeichnung, nicht je Knoten: die Menge der fertigen
    // Ausbaustufen ist fuer alle 47 Kacheln dieselbe.
    const status = treeStatus(store.save, store.statCard('enemiesByKind'));

    for (const entry of nodes()) {
      for (const reqId of entry.req) {
        const req = nodeById(reqId);
        if (!req) continue;
        const on = store.upgradeLevel(reqId) > 0;
        g.lineStyle(3, 0x14485a, on ? 0.75 : 0.28);
        g.beginPath();
        g.moveTo(req.x * CELL, req.y * CELL);
        if (entry.x !== req.x && entry.y !== req.y) g.lineTo(req.x * CELL, entry.y * CELL);
        g.lineTo(entry.x * CELL, entry.y * CELL);
        g.strokePath();
      }
    }

    for (const entry of nodes()) this.drawNode(entry, nodeState(entry, status));
  }

  private drawNode(entry: TreeNode, state: NodeState): void {
    const g = this.gfx;
    const level = store.upgradeLevel(entry.id);
    const x = entry.x * CELL - NODE_SIZE / 2;
    const y = entry.y * CELL - NODE_SIZE / 2;
    const style = NODE_STYLE[state];

    g.fillStyle(0x000000, 0.6);
    g.fillRect(x + 2, y + 3, NODE_SIZE, NODE_SIZE);
    g.fillStyle(style.fill, 1);
    g.fillRect(x, y, NODE_SIZE, NODE_SIZE);
    g.lineStyle(3, style.border, 1);
    g.strokeRect(x, y, NODE_SIZE, NODE_SIZE);
    // Der ausgebaute Knoten bekommt einen zweiten, inneren Rand: „versiegelt".
    // Er wirkt auch dort noch, wo die Farbe bei kleinem Zoom verschwimmt.
    if (state === 'max') g.strokeRect(x + 5, y + 5, NODE_SIZE - 10, NODE_SIZE - 10);

    if (this.selected === entry) {
      g.lineStyle(3, 0xffffff, 1);
      g.strokeRect(x - 5, y - 5, NODE_SIZE + 10, NODE_SIZE + 10);
    } else if (this.hovered === entry) {
      g.lineStyle(2, 0xffffff, 0.75);
      g.strokeRect(x - 3, y - 3, NODE_SIZE + 6, NODE_SIZE + 6);
    }

    const icon = this.icons.get(entry.id);
    if (icon) {
      icon.setTexture(iconTextureKey(state === 'locked' ? 'lock' : entry.icon));
      icon.setAlpha(style.iconAlpha);
    }

    // Die Ecke sagt „jetzt": sie erscheint nur, solange der Kauf gedeckt ist.
    if (state === 'buyable') {
      g.fillStyle(style.accent, 1);
      g.fillTriangle(x + 2, y + 2, x + 14, y + 2, x + 2, y + 14);
    }
    if (state !== 'locked') this.drawBar(entry, state, level, x, y);
    this.drawLevel(entry, state, level, x, y);
  }

  /**
   * Stufe und Hoechststufe als Kaestchenreihe.
   *
   * Sie ist das einzige Merkmal, das **beide** Fragen zugleich beantwortet:
   * „ist hier noch etwas offen" und „wie viel". Eine Zahl allein verlangt zu
   * wissen, wo diese Stufe endet — und das steht bei jedem Knoten anders.
   */
  private drawBar(entry: TreeNode, state: NodeState, level: number, x: number, y: number): void {
    const g = this.gfx;
    const width = NODE_SIZE - BAR_INSET * 2;
    const left = x + BAR_INSET;
    const top = y + NODE_SIZE - BAR_BOTTOM - BAR_HEIGHT;
    const gap = entry.max > 1 ? 1 : 0;
    const step = (width - gap * (entry.max - 1)) / entry.max;

    g.fillStyle(0x0a1016, 0.85);
    g.fillRect(left - 1, top - 1, width + 2, BAR_HEIGHT + 2);
    for (let i = 0; i < entry.max; i += 1) {
      g.fillStyle(i < level ? NODE_STYLE[state].accent : 0x2b3038, 1);
      g.fillRect(left + i * (step + gap), top, step, BAR_HEIGHT);
    }
  }

  private drawLevel(entry: TreeNode, state: NodeState, level: number, x: number, y: number): void {
    const label = this.labels.get(entry.id);
    if (!label) return;
    label.setText(level > 0 ? String(level) : '');
    label.setColor(state === 'max' ? MAX_TEXT : '#ffe98a');
    if (level <= 0) return;
    // Ein dunkles Feld hinter der Zahl: auf goldenem Grund waere sie sonst
    // genau dort unlesbar, wo sie am haeufigsten steht.
    this.gfx.fillStyle(0x0a1016, 0.85);
    this.gfx.fillRect(x + NODE_SIZE - 17, y + 2, 15, 14);
  }

}

/** Die Stufenzahl des ausgebauten Knotens spricht dieselbe Farbe wie sein Rand. */
const MAX_TEXT = cssColor(NODE_STYLE.max.accent);
